import { useState, useEffect, type ReactNode } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, getCountFromServer, limit, runTransaction } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { masterDb } from '../firebase/master';
import { useAuth } from '../context/AuthContext';
import { useWallpaper, WALLPAPERS, type WallpaperId } from '../context/WallpaperContext';
import { useAccent, ACCENT_PRESETS, DEFAULT_ACCENT } from '../context/AccentContext';
import AppLayout from '../components/AppLayout';
import { Modal, Button, FormField } from '../components/ui';
import { EntityCombobox } from '../components/ui/EntityCombobox';
import { TwoStepPanel } from '../components/TwoStepSecurity';
import BackupPanel from '../components/BackupPanel';
import { renewalBadge } from '../utils/renewal';
import { includedUsersOf, firstYearAmountOf, renewalAmountOf, extraUserRateOf, billingCurrencyOf, fmtBill } from '../utils/billing';
import { CURRENCY_LIST } from '../utils/currency';
import { COUNTRIES } from '../utils/countries';
import { PAGE_GROUPS } from '../utils/pages';
import { seedChartOfAccounts, rebuildLedgerFromHistory } from '../utils/ledger';
import { useSettings } from '../hooks/useSettings';
import { createDummyCode, renameDummyCode, DUMMY_CODE_RE } from '../utils/dummyCode';
import { tradingSystemOf, INDIAN_STATES, type TradingSystem } from '../utils/tradingSystem';
import { fmtMoney } from '../utils/currency';
import { TERMS_SECTIONS, TERMS_LAST_UPDATED, PRIVACY_SECTIONS, PRIVACY_LAST_UPDATED, SUPPORT_EMAIL, type LegalSection } from '../utils/legalContent';
import type { BusinessSettings, BankAccount, InventoryItem, BillingCurrency } from '../types';

const DEFAULT_LOOSE_COLORS    = ['D','E','F','G','H','I','J','K','L','M','Fancy'];
const DEFAULT_LOOSE_CLARITIES = ['FL','IF','VVS1','VVS2','VS1','VS2','SI1','SI2','I1','I2','I3'];

// Reuses EntityCombobox (built for {id, companyName} entities) as a
// type-to-filter country picker — see the identical technique in
// EntityFormModal.tsx.
const COUNTRY_OPTIONS = COUNTRIES.map(c => ({ id: c, companyName: c }));

// Downscales/compresses an image file client-side via <canvas> and
// returns it as a JPEG data URI — used for the invoice logo (see
// handleLogoFile below for why this is stored inline rather than
// uploaded to Firebase Storage).
function resizeImageToDataUrl(file: File, maxDim: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      img.onerror = () => reject(new Error('Could not decode image'));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('Canvas not supported')); return; }
        // JPEG has no alpha channel — without this, a transparent source
        // logo (the common case) gets composited onto the canvas's default
        // black backdrop on export, leaving a black box behind the logo
        // wherever it's shown (e.g. on the invoice document).
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

type SettingsTab = 'security' | 'general' | 'invoicing' | 'appearance' | 'custom' | 'team' | 'accounting' | 'usage' | 'backup' | 'subscription' | 'terms' | 'privacy';

function IconPlus() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>;
}

function IconX() {
  return <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>;
}

function IconImage() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="5.2" cy="6" r="1.3" stroke="currentColor" strokeWidth="1.3" />
    <path d="M2 11.5l3.5-3.5 2.5 2.5 2-2L14 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

function IconSettings() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.42 1.42M11.54 11.54l1.41 1.41M3.05 12.95l1.42-1.41M11.54 4.46l1.41-1.41"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

function IconInvoice() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h5.5L13 5v9.5H4Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M9 1.5V5h4M6 8h4M6 10.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}

function IconTune({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M2 4.5h5.5M10.5 4.5H14M2 11.5h3M8 11.5h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="9" cy="4.5" r="1.9" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="6.5" cy="11.5" r="1.9" stroke="currentColor" strokeWidth="1.4" />
  </svg>;
}

function IconGauge({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M2 12a6 6 0 1 1 12 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M8 12l3-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="12" r="1.1" fill="currentColor" />
  </svg>;
}

function IconArchive({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <rect x="1.8" y="2.5" width="12.4" height="3" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3 5.5v7a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-7" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.3 8.5h3.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

function IconCard({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4 9.5h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

function IconTeam({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <circle cx="5.5" cy="5" r="2.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="11.2" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.3 13.2c0-2.1 1.9-3.7 4.2-3.7s4.2 1.6 4.2 3.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M9.7 9.7c1.9.25 3.4 1.6 3.4 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}

function IconDocument({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h5.5L12.5 4.5V13.5a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M9.5 1.5V4.5H12.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5 8h6M5 10.5h6M5 5.5h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}

function IconShieldPrivacy({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M8 1.7l5 1.8v3.8c0 3.4-2.1 5.9-5 6.7-2.9-.8-5-3.3-5-6.7V3.5l5-1.8Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 8l1.7 1.7 3.3-3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

function IconChevronDown() {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── Option chips editor ──────────────────────────────────────────────────────

// ── Books Adjustment / Dummy code (Settings › Custom Options) ────────────────
// The owner can pick the Dummy code's Code up front instead of getting an
// auto-numbered one the first time a Cash/Real sale or Dummy purchase needs
// it (that lazy auto-create still exists as the fallback). Its Code can be
// changed only while it's unused — zero balance and no history — so its
// lifecycle history never ends up split across two Codes. The Code itself
// lives on the InventoryItem (resolved live here), never copied onto
// BusinessSettings. See utils/dummyCode.ts.
function DummyCodeCard({ dummyInventoryItemId, currency }: { dummyInventoryItemId: string; currency: string }) {
  const { business, user } = useAuth();
  const [liveItem, setLiveItem] = useState<InventoryItem | null>(null);
  // Only trust the snapshot for the id currently configured.
  const item = dummyInventoryItemId && liveItem?.id === dummyInventoryItemId ? liveItem : null;
  const [hasHistory, setHasHistory] = useState(true);   // assume used until proven otherwise
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !dummyInventoryItemId) return;
    const unsub = onSnapshot(doc(db, 'inventory', dummyInventoryItemId),
      snap => setLiveItem(snap.exists() ? ({ id: snap.id, ...snap.data() } as InventoryItem) : null));
    return unsub;
  }, [dummyInventoryItemId]);

  // Any history entry under its Code means it's been used — rename locked.
  useEffect(() => {
    const db = getClientDb();
    if (!db || !item?.codeId) return;
    let cancelled = false;
    getDocs(query(collection(db, 'itemCodes', item.codeId, 'history'), limit(1)))
      .then(snap => { if (!cancelled) setHasHistory(!snap.empty); })
      .catch(() => { if (!cancelled) setHasHistory(true); });
    return () => { cancelled = true; };
  }, [item?.codeId]);

  async function save() {
    const db = getClientDb();
    if (!db || !business || !user) return;
    setBusy(true); setError('');
    try {
      await runTransaction(db, tx => item
        ? renameDummyCode(tx, db, { itemId: item.id, newCode: draft, createdBy: user.id, hasHistory })
        : createDummyCode(tx, db, { businessId: business.id, code: draft, createdBy: user.id }));
      setEditing(false); setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the Dummy code.');
    }
    setBusy(false);
  }

  const used = !!item && (hasHistory || Math.abs(item.carat ?? 0) > 1e-9 || Math.abs(item.costUSD ?? 0) > 1e-6);
  const showForm = !item || editing;

  return (
    <>

      {item && !editing && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
            fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700,
            padding: '4px 10px', borderRadius: 6, letterSpacing: '0.02em',
          }}>
            {item.code || '—'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
            Balance <span style={{ fontFamily: 'var(--font-num)', color: (item.carat ?? 0) < 0 ? 'var(--danger)' : 'var(--text-1)' }}>
              {(item.carat ?? 0).toFixed(2)} ct · {fmtMoney(item.costUSD ?? 0, currency)}
            </span>
          </span>
          {used ? (
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>This code is in use, so it can't be changed.</span>
          ) : (
            <button type="button" className="line-item-link-btn" onClick={() => { setDraft(item.code ?? ''); setError(''); setEditing(true); }}>
              Change code
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div style={{ marginTop: item ? 4 : 0 }}>
          {/* Label above, then input + buttons on one row, all 36px tall so they line up. */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
            {item ? 'New Dummy Code' : 'Dummy Code'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={draft} placeholder="99999"
              inputMode="numeric" maxLength={5}
              // Digits only, at most 5 — same shape as the app's normal Codes.
              onChange={e => setDraft(e.target.value.replace(/\D/g, '').slice(0, 5))}
              onKeyDown={e => { if (e.key === 'Enter' && DUMMY_CODE_RE.test(draft)) save(); }}
              style={{
                width: 120, fontFamily: 'var(--font-num)', letterSpacing: '0.08em', height: 36, boxSizing: 'border-box', padding: '0 12px', fontSize: 13,
                border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--text-1)',
              }}
            />
            <Button variant="primary" onClick={save} loading={busy} disabled={!DUMMY_CODE_RE.test(draft)} style={{ height: 36, boxSizing: 'border-box' }}>
              {item ? 'Save' : 'Create Dummy Code'}
            </Button>
            {item && (
              <Button variant="secondary" onClick={() => { setEditing(false); setError(''); }} disabled={busy} style={{ height: 36, boxSizing: 'border-box' }}>
                Cancel
              </Button>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 6 }}>5 digits. Must be a new code.</div>
        </div>
      )}
      {error && <div className="msg msg-error" style={{ marginTop: 4 }}>{error}</div>}
    </>
  );
}

// ── Settings layout pieces ───────────────────────────────────────────────
// One section = title + a short note on the left, its fields on the right,
// a thin line between sections. Used by Invoicing, Custom Options,
// Team & Access and Accounting so every tab reads the same way.
function SettingsSection({ title, desc, wide, children }: { title: string; desc?: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <section className="set-section">
      <div className="set-section-head">
        <h4>{title}</h4>
        {desc && <p>{desc}</p>}
      </div>
      <div className={`set-section-body${wide ? ' set-section-wide' : ''}`}>{children}</div>
    </section>
  );
}

/** An image box: preview (or an empty "Upload" tile) with Change / Remove. */
function ImageUploadTile({ label, hint, value, onFile, onRemove, error, wide }: {
  label: string; hint?: string; value: string;
  onFile: (f: File | undefined) => void; onRemove: () => void; error?: string; wide?: boolean;
}) {
  const id = `upload-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="set-upload">
      <div className="set-upload-label">{label}</div>
      <label htmlFor={id} className={`set-upload-box${wide ? ' wide' : ''}${value ? ' has-img' : ''}`} title={value ? 'Change image' : 'Upload image'}>
        {value ? <img src={value} alt={`${label} preview`} /> : (
          <span className="set-upload-empty">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
              <circle cx="5.5" cy="6.5" r="1.3" stroke="currentColor" strokeWidth="1.2" />
              <path d="M2 12l4-4 3 3 2-2 3 3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            </svg>
            Upload
          </span>
        )}
      </label>
      <input id={id} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="set-upload-actions">
        <button type="button" onClick={() => document.getElementById(id)?.click()}>{value ? 'Change' : 'Upload'}</button>
        {value && <button type="button" className="danger" onClick={onRemove}>Remove</button>}
      </div>
      {hint && <div className="form-hint" style={{ margin: 0 }}>{hint}</div>}
      {error && <div className="form-hint" style={{ color: 'var(--danger)', margin: 0 }}>{error}</div>}
    </div>
  );
}

function OptionEditor({
  label, items, onAdd, onRemove, placeholder, inputMaxWidth,
}: {
  label: string;
  items: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  placeholder?: string;
  inputMaxWidth?: number;
}) {
  const [input, setInput] = useState('');

  function handleAdd() {
    const val = input.trim();
    if (!val || items.includes(val)) return;
    onAdd(val);
    setInput('');
  }

  // One full width row: name, box + Add on the left; the saved
  // values fill the rest of the width on the right (3 rows, then scroll).
  return (
    <div className="opt-editor">
      <div className="opt-editor-left">
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8, display: 'flex', alignItems: 'baseline', gap: 8 }}>
        {label}
        {items.length > 0 && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)' }}>{items.length} saved</span>}
      </div>

      {/* Add input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
          placeholder={placeholder ?? `e.g. ${label.includes('Color') ? 'G+, D-E, Fancy Pink'
            : label.includes('Sub Size') ? '+1.5, -2' : label.includes('Size') ? '+2, 1.00-1.20' : 'VVS1-VS2, SI1-SI2'}`}
          style={{
            flex: 1,
            maxWidth: inputMaxWidth,
            padding: '8px 12px',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--r-sm)',
            fontSize: 13,
            fontFamily: 'inherit',
            outline: 'none',
            color: 'var(--text-1)',
            background: 'var(--surface)',
          }}
        />
        <Button variant="secondary" size="sm" icon={<IconPlus />} onClick={handleAdd}>
          Add
        </Button>
      </div>
      </div>

      {/* Chips */}
      {/* Up to 3 rows of tags show; a longer list scrolls inside the box. */}
      <div className="opt-chips">
        {items.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-3)', alignSelf: 'center' }}>
            None yet. The normal list is used.
          </span>
        )}
        {items.map(item => (
          // Soft grey tags (the bright blue outlined pills were hard on the eyes
          // with many of them on one page); the × turns red on hover.
          <span key={item} className="opt-chip">
            {item}
            <button type="button" className="opt-chip-x" onClick={() => onRemove(item)} title={`Remove ${item}`}>
              <IconX />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Accent colour picker ────────────────────────────────────────────────────

function AccentPicker() {
  const { accent, setAccent } = useAccent();
  const current = (accent ?? DEFAULT_ACCENT).toLowerCase();
  const matchesPreset = ACCENT_PRESETS.some(p => p.value.toLowerCase() === current);

  return (
    <div>
      <div className="card-title"><IconImage /> Appearance: Accent Colour</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.6 }}>
        Colour of buttons and highlights. Only on this device.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        {ACCENT_PRESETS.map(p => {
          const selected = p.value.toLowerCase() === current;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setAccent(p.value === DEFAULT_ACCENT ? null : p.value)}
              title={p.name}
              aria-label={p.name}
              aria-pressed={selected}
              style={{
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                background: p.value,
                border: '2px solid var(--surface)',
                boxShadow: selected
                  ? '0 0 0 2px var(--surface), 0 0 0 4px var(--text-2)'
                  : '0 0 0 1px var(--border)',
                display: 'grid', placeItems: 'center',
              }}
            >
              {selected && (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3.5 8.5l3 3 6-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          );
        })}

        {/* Custom — native colour picker styled as a swatch */}
        <label
          title="Custom colour"
          style={{
            position: 'relative', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
            background: matchesPreset
              ? 'conic-gradient(from 0deg, #e11d48, #d97706, #059669, #2563eb, #7c3aed, #e11d48)'
              : current,
            border: '2px solid var(--surface)',
            boxShadow: !matchesPreset
              ? '0 0 0 2px var(--surface), 0 0 0 4px var(--text-2)'
              : '0 0 0 1px var(--border)',
          }}
        >
          <input
            type="color"
            value={current}
            onChange={e => setAccent(e.target.value)}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
          />
          {!matchesPreset && (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M3.5 8.5l3 3 6-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </label>

        {accent && (
          <Button variant="ghost" size="sm" onClick={() => setAccent(null)}>Reset to default</Button>
        )}
      </div>

      {/* Live preview */}
      <div style={{
        marginTop: 18, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
        background: 'var(--surface-2)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <Button variant="primary" size="sm">Primary button</Button>
        <span style={{
          fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-num)', letterSpacing: '0.02em',
          background: 'var(--primary-light)', color: 'var(--primary)', padding: '3px 8px', borderRadius: 6,
        }}>SAMPLE PILL</span>
        <a href="#preview" onClick={e => e.preventDefault()} style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>
          A link
        </a>
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontFamily: 'var(--font-num)' }}>{current}</span>
      </div>
    </div>
  );
}

// ── Wallpaper picker ─────────────────────────────────────────────────────────

function WallpaperPicker() {
  const { wallpaper, setWallpaper } = useWallpaper();

  const options: { id: WallpaperId; label: string; src?: string }[] = [
    { id: 'none', label: 'None' },
    ...WALLPAPERS.map((w, i) => ({ id: w.id, label: `Wallpaper ${i + 1}`, src: w.src })),
  ];

  return (
    <div>
      <div className="card-title"><IconImage /> Appearance: Wallpaper</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16, lineHeight: 1.6 }}>
        Background picture for the app. Only on this device.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {options.map(opt => {
          const selected = wallpaper === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setWallpaper(opt.id)}
              title={opt.label}
              style={{
                width: 120,
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{
                width: 120,
                height: 72,
                borderRadius: 'var(--r-sm)',
                border: selected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                overflow: 'hidden',
                background: opt.src ? `url(${opt.src}) center/cover` : 'var(--surface-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: selected ? '0 0 0 3px var(--primary-light)' : 'none',
              }}>
                {!opt.src && <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>No wallpaper</span>}
              </div>
              <div style={{
                marginTop: 6,
                fontSize: 12,
                fontWeight: 600,
                color: selected ? 'var(--primary)' : 'var(--text-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}>
                {selected && '✓ '}{opt.label}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Subscription — read-only, managed by DiaWolt support via the admin panel ─
// renewalBadge() now lives in src/utils/renewal.ts, shared with AdminPanel.tsx.

function fmtDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// In the company's billing currency (€, $, AED or ₹; see utils/billing.ts).
function money(v: number | null | undefined, cur: BillingCurrency) {
  if (v == null) return '—';
  return fmtBill(v, cur);
}

// ── Accounting — one-time chart-of-accounts setup ─────────────────────────
// Plain language on purpose: the owner is a trader, not an accountant. This
// panel just makes sure the background bookkeeping categories exist. The
// actual double-entry engine posts itself from Phase A2 onward.
function AccountingPanel() {
  const { user, business } = useAuth();
  const settings = useSettings();
  const [ledgerCount, setLedgerCount] = useState<number | null>(null);
  const [entryCount, setEntryCount]   = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const u1 = onSnapshot(
      collection(db, 'ledgerAccounts'),
      snap => setLedgerCount(snap.size),
      () => setLedgerCount(0),
    );
    const u2 = onSnapshot(
      collection(db, 'journalEntries'),
      snap => setEntryCount(snap.size),
      () => setEntryCount(0),
    );
    return () => { u1(); u2(); };
  }, [business]);

  async function rebuild() {
    const db = getClientDb();
    if (!db || !business) return;
    setRebuilding(true); setMsg(''); setErr('');
    try {
      const res = await rebuildLedgerFromHistory(
        db, business.id, settings.currency, user?.id ?? 'system',
        startDate ? { startDate } : undefined,
      );
      setMsg(
        `Rebuilt ${res.entries} entr${res.entries === 1 ? 'y' : 'ies'}` +
        (res.seeded ? ` (added ${res.seeded} missing account${res.seeded === 1 ? '' : 's'})` : '') +
        `. The books ${res.balanced ? 'balance ✓' : `do NOT balance (Dr ${res.totalDebit.toFixed(2)} vs Cr ${res.totalCredit.toFixed(2)})`}.`,
      );
      if (!res.balanced) setErr('The rebuilt books do not balance. Please report this.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Rebuild failed. Check the connection and try again.');
    }
    setRebuilding(false);
  }

  async function run() {
    const db = getClientDb();
    if (!db || !business) return;
    setRunning(true); setMsg(''); setErr('');
    try {
      const bankSnap = await getDocs(
        collection(db, 'bankAccounts'),
      );
      const banks = bankSnap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount));
      const res = await seedChartOfAccounts(
        db, business.id,
        banks.map(b => ({ id: b.id, name: b.name, currency: b.currency, type: b.type, isActive: b.isActive })),
      );
      setMsg(
        res.created > 0
          ? `Done. Added ${res.created} account${res.created === 1 ? '' : 's'}.`
          : 'Everything is already set up. Nothing to add.',
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Setup failed. Check the connection and try again.');
    }
    setRunning(false);
  }

  const isSet = (ledgerCount ?? 0) > 0;

  return (
    <div>
      <div className="card-title"><IconCard /> Accounting</div>
      
      <SettingsSection title="Chart of Accounts">
        <div className="set-status-row">
          <span className={`set-pill ${isSet ? 'ok' : 'warn'}`}>
            {ledgerCount === null ? 'Checking…' : isSet ? 'Set up' : 'Not set up yet'}
          </span>
          {ledgerCount !== null && (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{ledgerCount} account{ledgerCount === 1 ? '' : 's'} on file</span>
          )}
          <Button variant={isSet ? 'secondary' : 'primary'} size="sm" onClick={run} loading={running} style={{ marginLeft: 'auto' }}>
            {isSet ? 'Repair / Add Missing' : 'Set Up Now'}
          </Button>
        </div>
      </SettingsSection>

      {isSet && (
        <SettingsSection
          title="Rebuild the Books"
          desc={entryCount !== null && entryCount > 0 ? `${entryCount} entr${entryCount === 1 ? 'y' : 'ies'}` : undefined}
        >
          <div className="set-status-row">
            <label className="tb-field">
              <span className="tb-field-label">Only from</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} aria-label="Only from this date (optional)" />
            </label>
            <Button variant="secondary" size="sm" onClick={rebuild} loading={rebuilding} style={{ marginLeft: 'auto' }}>
              Rebuild Now
            </Button>
          </div>
        </SettingsSection>
      )}

      {(msg || err) && (
        <div className={err ? 'error-msg' : 'info-msg'} style={{ marginTop: 14 }}>{err || msg}</div>
      )}
    </div>
  );
}

// ── Firebase usage panel ────────────────────────────────────────────────────
// Firestore does NOT expose billed read/write counts to a client app — those
// live only in the Firebase Console (and Google Cloud Monitoring, which needs
// server credentials). What we CAN show accurately is the data footprint:
// document counts per collection, via cheap count() aggregation queries. From
// those we estimate how many reads a normal day of browsing costs.
const USAGE_COLLECTIONS: { key: string; label: string }[] = [
  { key: 'inventory',        label: 'Inventory items' },
  { key: 'invoices',         label: 'Purchase invoices' },
  { key: 'sales',            label: 'Sales invoices' },
  { key: 'entities',         label: 'Companies / customers' },
  { key: 'journalEntries',   label: 'Ledger entries' },
  { key: 'bankTransactions', label: 'Bank transactions' },
  { key: 'expenses',         label: 'Expenses' },
];
const FREE_READS_PER_DAY = 50000;
const FREE_WRITES_PER_DAY = 20000;

function UsagePanel() {
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    Promise.all(
      USAGE_COLLECTIONS.map(c =>
        getCountFromServer(collection(db, c.key)).then(s => [c.key, s.data().count] as const),
      ),
    )
      .then(entries => { if (!cancelled) setCounts(Object.fromEntries(entries)); })
      .catch(e => { if (!cancelled) setErr(e instanceof Error ? e.message : 'Could not read usage.'); });
    return () => { cancelled = true; };
  }, []);

  const total = counts ? Object.values(counts).reduce((s, n) => s + n, 0) : 0;
  // The three big list pages each load their whole collection on open.
  const perBrowse = counts
    ? (counts.inventory ?? 0) + (counts.invoices ?? 0) + (counts.sales ?? 0)
    : 0;
  const dayEstimate = perBrowse * 2 * 5; // 2 people × ~5 full-list opens each

  return (
    <div>
      <div className="card-title"><IconGauge /> Firebase Usage</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 18, lineHeight: 1.6 }}>
        Free each day: <strong>{FREE_READS_PER_DAY.toLocaleString()} reads</strong> and{' '}
        <strong>{FREE_WRITES_PER_DAY.toLocaleString()} writes</strong>. These numbers are an estimate.
      </div>

      {err && <div className="error-msg" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="card-title" style={{ fontSize: 13, marginBottom: 8 }}>Data footprint</div>
      <div className="table-wrap" style={{ marginBottom: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Collection</th>
              <th style={{ textAlign: 'right', width: 140 }}>Documents</th>
            </tr>
          </thead>
          <tbody>
            {USAGE_COLLECTIONS.map(c => (
              <tr key={c.key}>
                <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{c.label}</td>
                <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-1)', fontWeight: 600 }}>
                  {counts ? counts[c.key].toLocaleString() : '…'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--surface-2)' }}>
              <td style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>Total documents</td>
              <td style={{ fontSize: 12, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>
                {counts ? total.toLocaleString() : '…'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {counts && (
        <div style={{
          border: '1px solid var(--border)', borderLeft: '3px solid var(--primary)',
          borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', padding: '14px 16px',
          fontSize: 13, lineHeight: 1.7, color: 'var(--text-2)',
        }}>
          <strong style={{ color: 'var(--text-1)' }}>Rough daily read estimate</strong><br />
          Opening the Inventory list loads about <strong>{(counts.inventory ?? 0).toLocaleString()}</strong> reads,
          Purchases <strong>{(counts.invoices ?? 0).toLocaleString()}</strong>, Sales <strong>{(counts.sales ?? 0).toLocaleString()}</strong>.
          A day of two people each opening the main lists ~5 times is roughly{' '}
          <strong style={{ color: dayEstimate > FREE_READS_PER_DAY ? 'var(--danger)' : 'var(--text-1)' }}>
            {dayEstimate.toLocaleString()} reads
          </strong>
          {'. '}{dayEstimate > FREE_READS_PER_DAY
            ? 'That is more than the free limit.'
            : 'That is within the free limit.'}
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>
            Estimate only.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Terms of Service / Privacy Policy — plain informational panels, no
// editable state and no Save button (see the shared-footer exclusion at the
// bottom of the page). Content lives in utils/legalContent.ts, kept
// separate from this rendering shell so the legal text is easy to find and
// review on its own.

// Bolds every occurrence of the support email within a paragraph — done
// here at render time (splitting on the plain-text constant) rather than
// hand-marking it in the content strings, so legalContent.ts stays plain
// prose and this stays the one place that controls how it's emphasized.
function withBoldEmail(text: string): React.ReactNode {
  if (!text.includes(SUPPORT_EMAIL)) return text;
  return text.split(SUPPORT_EMAIL).flatMap((part, i, arr) => (
    i < arr.length - 1 ? [part, <strong key={i}>{SUPPORT_EMAIL}</strong>] : [part]
  ));
}

// One section, click-to-expand — reuses Landing.tsx's FAQ accordion CSS
// (.landing-faq-*) verbatim rather than inventing a parallel set: it's
// already theme-token-based (var(--surface)/--border/--primary/--text-*),
// so it looks native here too, and its grid-template-rows animation is the
// same "smooth height, no JS measurement" technique already established.
// Open state lives in LegalDoc, so opening one section closes the other.
function LegalAccordionItem({ heading, body, open, onToggle }: { heading: string; body: string[]; open: boolean; onToggle: () => void }) {
  return (
    <div className={`landing-faq-item${open ? ' open' : ''}`}>
      <button type="button" className="landing-faq-question" onClick={onToggle} aria-expanded={open}>
        {heading}
        <span className="landing-faq-caret"><IconChevronDown /></span>
      </button>
      <div className="landing-faq-answer-wrap">
        <div className="landing-faq-answer-inner">
          {body.map((p, i) => (
            <p key={i} className="landing-faq-answer">{withBoldEmail(p)}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

function LegalDoc({ icon, title, updated, sections }: {
  icon: React.ReactNode; title: string; updated: string; sections: LegalSection[];
}) {
  // Only one section open at a time; clicking the open one closes it.
  const [openHeading, setOpenHeading] = useState<string | null>(null);
  return (
    <div>
      <div className="card-title">{icon} {title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 20 }}>Last updated: {updated}</div>
      <div className="landing-faq-list" style={{ maxWidth: 'none', margin: 0 }}>
        {sections.map(s => (
          <LegalAccordionItem key={s.heading} heading={s.heading} body={s.body}
            open={openHeading === s.heading}
            onToggle={() => setOpenHeading(cur => (cur === s.heading ? null : s.heading))} />
        ))}
      </div>
    </div>
  );
}

function TermsPanel() {
  return <LegalDoc icon={<IconDocument />} title="Terms of Service" updated={TERMS_LAST_UPDATED} sections={TERMS_SECTIONS} />;
}

function PrivacyPanel() {
  return <LegalDoc icon={<IconShieldPrivacy />} title="Privacy Policy" updated={PRIVACY_LAST_UPDATED} sections={PRIVACY_SECTIONS} />;
}

// Time through the subscription period and days to renewal, in local
// calendar days (same rule as the Admin Portal's subProgress).
function subscriptionProgress(start?: string, end?: string) {
  if (!end) return null;
  const day = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); };
  const endD = day(end);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const startD = start ? day(start) : new Date(endD.getFullYear() - 1, endD.getMonth(), endD.getDate());
  const total = Math.max(1, Math.round((endD.getTime() - startD.getTime()) / 86_400_000));
  const used = Math.min(total, Math.max(0, Math.round((today.getTime() - startD.getTime()) / 86_400_000)));
  const daysLeft = Math.round((endD.getTime() - today.getTime()) / 86_400_000);
  return { pct: Math.round((used / total) * 100), used, total, daysLeft };
}

/** "2 of 3" (or "3 + 1 extra") with one small block per seat under it:
 *  in use = primary, free = empty, extra = amber. */
function UsersSeats({ included, active }: { included: number; active: number | null }) {
  if (active === null) return <>{included} included</>;
  const extra = Math.max(0, active - included);
  const slots = Math.max(active, included);
  const used  = Math.min(active, included);
  return (
    <div>
      <span style={{ fontFamily: 'var(--font-num)', color: extra > 0 ? 'var(--warning)' : undefined }}>
        {extra > 0 ? `${included} + ${extra} extra` : `${active} of ${included}`}
      </span>
      <div title={`${active} active, ${included} included`} style={{ display: 'flex', gap: 3, marginTop: 6, maxWidth: 120 }}>
        {Array.from({ length: slots }, (_, i) => (
          <div key={i} style={{
            flex: 1, height: 5, borderRadius: 999, boxSizing: 'border-box',
            background: i < used ? 'var(--primary)' : i < included ? 'var(--surface)' : 'var(--warning)',
            border: i < used || i >= included ? 'none' : '1px solid var(--border)',
          }} />
        ))}
      </div>
    </div>
  );
}

function SubscriptionPanel() {
  const { business } = useAuth();
  // Active logins of this company (owner + staff), for the Users bar.
  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  useEffect(() => {
    if (!business) return;
    const q = query(collection(masterDb, 'users'), where('businessId', '==', business.id));
    return onSnapshot(q, snap => {
      setActiveUsers(snap.docs.filter(d => (d.data() as StaffUser).isActive !== false).length);
    }, () => setActiveUsers(null));
  }, [business]);
  const badge = renewalBadge(business?.subscriptionEnd);
  const hasData = !!(business?.subscriptionStart || business?.subscriptionEnd || business?.baseAmount != null);
  const prog = subscriptionProgress(business?.subscriptionStart, business?.subscriptionEnd);
  const bcur = billingCurrencyOf(business);
  const tone = !prog ? 'var(--text-3)' : prog.daysLeft < 0 ? 'var(--danger)' : prog.daysLeft <= 30 ? 'var(--warning)' : 'var(--success)';

  return (
    <div>
      <div className="card-title"><IconCard /> Subscription</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.6 }}>
        Your plan. Contact DiaWolt support to change it.
      </div>

      {!hasData ? (
        <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '10px 0' }}>
          No subscription data on file yet.
        </div>
      ) : (
        <>
          {/* Progress from Subscription Start to Renewal Date: green, amber
              in the last 30 days, red once overdue. */}
          {prog ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', maxWidth: 560,
              border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 12px',
              marginBottom: 12, fontSize: 12, color: 'var(--text-3)',
            }}>
              <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(business?.subscriptionStart)}</span>
              <div title={`${prog.used} of ${prog.total} days`}
                style={{ flex: '1 1 120px', height: 6, borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)', overflow: 'hidden', boxSizing: 'border-box' }}>
                <div style={{ width: `${prog.pct}%`, height: '100%', background: tone, borderRadius: 999 }} />
              </div>
              <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(business?.subscriptionEnd)}</span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: 700, color: tone }}>
                {prog.daysLeft < 0 ? `${-prog.daysLeft} day${prog.daysLeft === -1 ? '' : 's'} overdue`
                  : prog.daysLeft === 0 ? 'Renews today'
                  : `${prog.daysLeft} day${prog.daysLeft === 1 ? '' : 's'} left`}
              </span>
              {badge && (
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '2px 8px', borderRadius: 999, background: badge.bg, color: badge.color, border: badge.border, whiteSpace: 'nowrap',
                }}>
                  {badge.label}
                </span>
              )}
            </div>
          ) : badge && (
            <span style={{
              display: 'inline-block', marginBottom: 16,
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
              padding: '4px 10px', borderRadius: 999,
              background: badge.bg, color: badge.color, border: badge.border,
            }}>
              {badge.label}
            </span>
          )}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1,
            border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
            overflow: 'hidden', marginBottom: 4,
          }}>
            {[
              ...(prog ? [] : [
                { label: 'Subscription Start', val: fmtDate(business?.subscriptionStart) },
                { label: 'Renewal Date',        val: fmtDate(business?.subscriptionEnd) },
              ]),
              { label: 'First Year',          val: business ? money(firstYearAmountOf(business), bcur) : '—' },
              { label: 'Renewal / Year',      val: business ? (() => {
                // Extra users above the included seats are added to the renewal.
                const base  = renewalAmountOf(business);
                const rate  = extraUserRateOf(business);
                const extra = activeUsers === null ? 0 : Math.max(0, activeUsers - includedUsersOf(business));
                if (extra === 0) return money(base, bcur);
                return (
                  <div>
                    {money(base + extra * rate, bcur)}
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-3)', marginTop: 2, fontFamily: 'var(--font-num)' }}>
                      {money(base, bcur)} + {extra} × {money(rate, bcur)}
                    </div>
                  </div>
                );
              })() : '—' },
              { label: 'Users',               val: business ? <UsersSeats included={includedUsersOf(business)} active={activeUsers} /> : '—' },
              { label: 'Extra User / Year',   val: business ? money(extraUserRateOf(business), bcur) : '—' },
            ].map(({ label, val }: { label: string; val: React.ReactNode }) => (
              <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)' }}>{val}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Team & Access — owner-only. Staff logins are still created by DiaWolt ──
// support via the Admin Portal (unchanged) — this tab is for the Owner to
// choose exactly which pages each existing staff login can see, and to
// disable one, without needing to ask DiaWolt to do it. No role presets.
// See utils/pages.ts's PAGE_GROUPS, the same registry the sidebar nav and
// route guard read from.

interface StaffUser {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'staff';
  isActive: boolean;
  allowedPages?: string[];
}

function PageAccessPicker({ selected, onToggle, onToggleMany }: {
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleMany: (keys: string[], on: boolean) => void;
}) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
      {PAGE_GROUPS.map(group => (
        <div key={group.label}>
          <div className="dropdown-group-label">{group.label}</div>
          {group.pages.map(p => {
            // Flat leaf — a plain checkbox, as before.
            if (!p.children) {
              return (
                <label key={p.key} className="dropdown-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(p.key)}
                    onChange={() => onToggle(p.key)}
                    style={{ marginRight: 8 }}
                  />
                  {p.label}
                </label>
              );
            }
            // Module parent — a "whole module" toggle + one checkbox per
            // grantable child. Accountant-only leaves are never staff-grantable,
            // so they're omitted here entirely.
            const grantable = p.children.filter(c => !c.accountantOnly);
            if (grantable.length === 0) return null;
            const onCount = grantable.filter(c => selected.has(c.key)).length;
            const allOn = onCount === grantable.length;
            return (
              <div key={p.key}>
                <label className="dropdown-item" style={{ cursor: 'pointer', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={el => { if (el) el.indeterminate = onCount > 0 && !allOn; }}
                    onChange={() => onToggleMany(grantable.map(c => c.key), !allOn)}
                    style={{ marginRight: 8 }}
                  />
                  {p.label}
                  <span style={{ opacity: 0.5, fontWeight: 400, marginLeft: 5 }}>· whole module</span>
                </label>
                {grantable.map(c => (
                  <label key={c.key} className="dropdown-item" style={{ cursor: 'pointer', paddingLeft: 30 }}>
                    <input
                      type="checkbox"
                      checked={selected.has(c.key)}
                      onChange={() => onToggle(c.key)}
                      style={{ marginRight: 8 }}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function EditAccessModal({ staffUser, onClose }: { staffUser: StaffUser; onClose: () => void }) {
  const [pages, setPages] = useState<Set<string>>(new Set(staffUser.allowedPages ?? []));
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState<string | null>(null);

  function togglePage(key: string) {
    setPages(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleMany(keys: string[], on: boolean) {
    setPages(prev => {
      const next = new Set(prev);
      keys.forEach(k => { if (on) next.add(k); else next.delete(k); });
      return next;
    });
  }

  async function handleSave() {
    setSaving(true); setErr(null);
    try {
      await updateDoc(doc(masterDb, 'users', staffUser.id), { allowedPages: Array.from(pages) });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit Access: ${staffUser.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>Save Access</Button>
        </>
      }
    >
      {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
      <PageAccessPicker selected={pages} onToggle={togglePage} onToggleMany={toggleMany} />
    </Modal>
  );
}

function TeamPanel() {
  const { business } = useAuth();
  const [staff, setStaff]         = useState<StaffUser[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [editing, setEditing]     = useState<StaffUser | null>(null);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!business) return;
    const q = query(collection(masterDb, 'users'), where('businessId', '==', business.id));
    const unsub = onSnapshot(q, snap => {
      const rows = snap.docs
        .map(d => ({ id: d.id, ...d.data() } as StaffUser))
        .filter(u => u.role === 'staff');
      setStaff(rows);
      setLoadingStaff(false);
    }, err => {
      setError(`Cannot load team: ${err.message}`);
      setLoadingStaff(false);
    });
    return unsub;
  }, [business]);

  async function toggleActive(u: StaffUser) {
    try {
      await updateDoc(doc(masterDb, 'users', u.id), { isActive: !u.isActive });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update.');
    }
  }

  return (
    <div>
      <div className="card-title"><IconTeam /> Team &amp; Access</div>
      <p className="set-intro">New logins come from DiaWolt support.</p>

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <SettingsSection
        title="Staff"
        desc={staff.length > 0 ? `${staff.filter(u => u.isActive).length} of ${staff.length} active` : undefined}
      >
        {loadingStaff ? (
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Loading team…</div>
        ) : staff.length === 0 ? (
          <div className="set-note">No staff logins yet. Contact DiaWolt support to add one.</div>
        ) : (
          <div className="set-team">
            {staff.map(u => {
              const count = u.allowedPages?.length ?? 0;
              const initials = u.name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';
              return (
                <div key={u.id} className={`set-team-row${u.isActive ? '' : ' off'}`}>
                  <span className="set-team-avatar" aria-hidden="true">{initials}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="set-team-name">{u.name}</div>
                    <div className="set-team-email">{u.email}</div>
                  </div>
                  <span className={`set-pill ${u.isActive ? 'ok' : 'off'}`}>{u.isActive ? 'Active' : 'Disabled'}</span>
                  <span className="set-pill">{u.allowedPages === undefined ? 'All pages' : `${count} page${count === 1 ? '' : 's'}`}</span>
                  <Button variant="secondary" size="sm" onClick={() => setEditing(u)}>Edit Access</Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleActive(u)}
                    style={{ color: u.isActive ? 'var(--danger)' : 'var(--success)' }}>
                    {u.isActive ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </SettingsSection>

      {editing && (
        <EditAccessModal staffUser={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { business, user } = useAuth();

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  const [currency,         setCurrency]         = useState('USD');
  const [certifiedPrefix,  setCertifiedPrefix]  = useState('PC');
  const [loosePrefix,      setLoosePrefix]      = useState('PL');
  const [salesPrefix,      setSalesPrefix]      = useState('SL');
  const [itemCodeStart,    setItemCodeStart]    = useState('20000');
  const [looseColors,      setLooseColors]      = useState<string[]>([]);
  const [looseClarities,   setLooseClarities]   = useState<string[]>([]);
  const [looseSizes,       setLooseSizes]       = useState<string[]>([]);
  const [looseSubSizes,    setLooseSubSizes]    = useState<string[]>([]);
  const [saleDescriptions, setSaleDescriptions] = useState<string[]>([]);
  // Trading System: Belgian (default) or Indian (Less 1 / Less 2 + GST).
  // The company's own old choice; the Admin Portal's (company record) wins.
  const [ownSystem,        setTradingSystem]    = useState<TradingSystem>('belgian');
  const tradingSystem: TradingSystem = business?.tradingSystem ?? ownSystem;
  const [gstRate,          setGstRate]          = useState('');
  const [gstState,         setGstState]         = useState('');
  const [gstin,            setGstin]            = useState('');
  // Picking the other system asks first (what will change).
  // Read-only — see TransactionCategory / BusinessSettings.dummyInventoryItemId.
  // Never edited from this form; dummyCode is resolved live from the one
  // InventoryItem doc it points at, in a separate effect below.
  const [dummyInventoryItemId, setDummyInventoryItemId] = useState('');
  // ── Invoicing (Settings › Invoicing) — the business's own details as they
  // should appear on a printed customer invoice. See BusinessSettings in
  // types/index.ts for why these are flat fields here rather than reusing
  // the EntityAddress shape.
  const [invCompanyName,  setInvCompanyName]  = useState('');
  const [invAddressLine1, setInvAddressLine1] = useState('');
  const [invAddressLine2, setInvAddressLine2] = useState('');
  const [invCity,         setInvCity]         = useState('');
  const [invPostalCode,   setInvPostalCode]   = useState('');
  const [invCountry,      setInvCountry]      = useState('');
  const [invVatNumber,    setInvVatNumber]    = useState('');
  const [invEmail,        setInvEmail]        = useState('');
  const [invPhone,        setInvPhone]        = useState('');
  const [invBankAccountId, setInvBankAccountId] = useState('');
  const [invFooterNote,   setInvFooterNote]   = useState('');
  const [invLogoDataUrl,  setInvLogoDataUrl]  = useState('');
  const [logoError,       setLogoError]       = useState('');
  const [invRjcCertDataUrl, setInvRjcCertDataUrl] = useState('');
  const [rjcCertError,      setRjcCertError]      = useState('');
  const [bankAccounts,    setBankAccounts]    = useState<BankAccount[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      doc(db, 'settings', business.id),
      snap => {
        if (snap.exists()) {
          const d = snap.data() as BusinessSettings;
          setCurrency(d.currency ?? 'USD');
          setCertifiedPrefix(d.certifiedPrefix ?? 'PC');
          setLoosePrefix(d.loosePrefix ?? 'PL');
          setSalesPrefix(d.salesPrefix ?? 'SL');
          setItemCodeStart(String(d.itemCodeStart ?? 20000));
          setLooseColors(d.looseColorOptions ?? []);
          setLooseClarities(d.looseClarityOptions ?? []);
          setLooseSizes(d.looseSizeOptions ?? []);
          setLooseSubSizes(d.looseSubSizeOptions ?? []);
          setSaleDescriptions(d.saleLineDescriptions ?? []);
          setTradingSystem(tradingSystemOf(d));
          setGstRate(d.gst?.defaultRate != null ? String(d.gst.defaultRate) : '');
          setGstState(d.gst?.state ?? '');
          setGstin(d.gst?.gstin ?? '');
          setDummyInventoryItemId(d.dummyInventoryItemId ?? '');
          setInvCompanyName(d.invoiceCompanyName ?? '');
          setInvAddressLine1(d.invoiceAddressLine1 ?? '');
          setInvAddressLine2(d.invoiceAddressLine2 ?? '');
          setInvCity(d.invoiceCity ?? '');
          setInvPostalCode(d.invoicePostalCode ?? '');
          setInvCountry(d.invoiceCountry ?? '');
          setInvVatNumber(d.invoiceVatNumber ?? '');
          setInvEmail(d.invoiceEmail ?? '');
          setInvPhone(d.invoicePhone ?? '');
          setInvBankAccountId(d.invoiceBankAccountId ?? '');
          setInvFooterNote(d.invoiceFooterNote ?? '');
          setInvLogoDataUrl(d.invoiceLogoDataUrl ?? '');
          setInvRjcCertDataUrl(d.invoiceRjcCertDataUrl ?? '');
        }
        setLoading(false);
      },
      err => {
        setSaveError(`Cannot read settings: ${err.message}. Check the Firestore rules for the "settings" collection.`);
        setLoading(false);
      },
    );
    return unsub;
  }, [business]);


  // Live bank-account list, for the "which account prints on the invoice"
  // picker below — independent of the one-off getDocs fetch further down
  // (Chart of Accounts seeding), which isn't reactive and isn't scoped to UI.
  useEffect(() => {
    const db = getClientDb();
    if (!db || !business) return;
    const unsub = onSnapshot(
      collection(db, 'bankAccounts'),
      snap => setBankAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount)).filter(a => a.isActive !== false)),
    );
    return unsub;
  }, [business]);

  // Resizes/compresses a picked file client-side (max 320px on the long
  // edge, JPEG ~0.85 quality) and stores it as a data URI directly on this
  // save form's own state — no Firebase Storage upload. This repo has no
  // Storage usage anywhere else, and each tenant's Storage bucket/rules
  // are provisioned outside this repo (same reason most Firestore rules
  // are too), so there'd be no way to verify a Storage upload even works.
  // Both the logo and the RJC certificate badge only need to render inline
  // in the invoice doc, so keeping them in the same `settings` doc
  // everything else already lives in avoids that whole unverifiable
  // dependency. Rejects a result over ~500KB to stay well under
  // Firestore's 1MB document cap alongside every other setting on this doc.
  async function handleImageFile(
    file: File | undefined,
    setDataUrl: (v: string) => void,
    setError: (v: string) => void,
  ) {
    setError('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 320, 0.85);
      if (dataUrl.length > 500_000) {
        setError('That image is too large, even after compressing. Try a smaller or simpler image.');
        return;
      }
      setDataUrl(dataUrl);
    } catch {
      setError('Could not read that image. Try a different file.');
    }
  }

  async function handleSave() {
    const db = getClientDb();
    if (!db || !business) return;
    setSaving(true); setSaved(false);
    try {
      await setDoc(doc(db, 'settings', business.id), {
        businessId: business.id,
        currency,
        certifiedPrefix: certifiedPrefix.trim().toUpperCase() || 'PC',
        loosePrefix:     loosePrefix.trim().toUpperCase()     || 'PL',
        salesPrefix:     salesPrefix.trim().toUpperCase()     || 'SL',
        itemCodeStart:   parseInt(itemCodeStart, 10) || 0,
        looseColorOptions: looseColors,
        looseClarityOptions: looseClarities,
        looseSizeOptions: looseSizes,
        looseSubSizeOptions: looseSubSizes,
        saleLineDescriptions: saleDescriptions,
        gst: {
          defaultRate: parseFloat(gstRate) > 0 ? parseFloat(gstRate) : null,
          state: gstState || null,
          gstin: gstin.trim().toUpperCase() || null,
        },
        invoiceCompanyName:  invCompanyName.trim()  || null,
        invoiceAddressLine1: invAddressLine1.trim() || null,
        invoiceAddressLine2: invAddressLine2.trim() || null,
        invoiceCity:         invCity.trim()         || null,
        invoicePostalCode:   invPostalCode.trim()   || null,
        invoiceCountry:      invCountry.trim()      || null,
        invoiceVatNumber:    invVatNumber.trim()    || null,
        invoiceEmail:        invEmail.trim()        || null,
        invoicePhone:        invPhone.trim()        || null,
        invoiceBankAccountId: invBankAccountId      || null,
        invoiceFooterNote:   invFooterNote.trim()   || null,
        invoiceLogoDataUrl:  invLogoDataUrl         || null,
        invoiceRjcCertDataUrl: invRjcCertDataUrl    || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSaved(true); setSaveError('');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save. Check Firestore rules for the "settings" collection.');
    }
    setSaving(false);
  }

  const TABS: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { key: 'general',      label: 'General',       icon: <IconSettings /> },
    { key: 'invoicing',    label: 'Invoicing',     icon: <IconInvoice /> },
    { key: 'appearance',   label: 'Appearance',     icon: <IconImage /> },
    { key: 'custom',       label: 'Custom Options', icon: <IconTune /> },
    // Staff can open Settings too, but never Team & Access (where access
    // is granted), Accounting or Subscription: those stay owner only.
    ...(user?.role === 'owner' ? [{ key: 'team' as const, label: 'Team & Access', icon: <IconTeam /> }] : []),
    ...(user?.role === 'owner' ? [{ key: 'accounting' as const, label: 'Accounting', icon: <IconCard /> }] : []),
    { key: 'usage',        label: 'Usage',          icon: <IconGauge /> },
    // Backup holds every record incl. costs, and Restore replaces everyone's data: owner only.
    ...(user?.role === 'owner' ? [{ key: 'backup' as const, label: 'Backup & Restore', icon: <IconArchive /> }] : []),
    { key: 'security',     label: 'Security',       icon: <IconShieldPrivacy /> },
    ...(user?.role === 'owner' ? [{ key: 'subscription' as const, label: 'Subscription', icon: <IconCard /> }] : []),
    // Informational only — visible to every user (Owner or Staff), not just
    // owner-only tabs like Team/Accounting/Subscription, since anyone
    // using the app should be able to read what they agreed to.
    { key: 'terms',        label: 'Terms of Service', icon: <IconDocument /> },
    { key: 'privacy',      label: 'Privacy Policy',   icon: <IconShieldPrivacy /> },
  ];

  // Editing a General/Custom Options field doesn't need re-fetching anything —
  // just local state saved together via one Save button below, shared across
  // every tab (not per-tab) so switching tabs never loses a pending edit.
  return (
    <AppLayout title="Settings" subtitle="Configure your business preferences">
      <div className="settings-shell">
        <nav className="settings-nav">
          {TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`settings-nav-item${activeTab === tab.key ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="settings-content">
          {loading ? (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '32px 0' }}>Loading settings…</div>
          ) : (
          <>
          {activeTab === 'general' && (
            <div>
              <div className="card-title"><IconSettings /> General</div>

              <div className="settings-general-grid">
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Currency</label>
                  <select value={currency} onChange={e => setCurrency(e.target.value)}
                    style={{ width: '100%', minWidth: 100, padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: 13, fontFamily: 'inherit', background: 'var(--surface)', color: 'var(--text-1)', outline: 'none', appearance: 'none' }}>
                    {CURRENCY_LIST.map(c => <option key={c.code} value={c.code}>{c.code}: {c.name}</option>)}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                    Certified Prefix
                  </label>
                  <input
                    value={certifiedPrefix}
                    onChange={e => setCertifiedPrefix(e.target.value.toUpperCase())}
                    maxLength={6}
                    placeholder="PC"
                    style={{ width: '100%', minWidth: 70, padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: 13, fontFamily: 'var(--font-num)', background: 'var(--surface)', color: 'var(--text-1)', outline: 'none', fontWeight: 600 }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                    Loose Prefix
                  </label>
                  <input
                    value={loosePrefix}
                    onChange={e => setLoosePrefix(e.target.value.toUpperCase())}
                    maxLength={6}
                    placeholder="PL"
                    style={{ width: '100%', minWidth: 70, padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: 13, fontFamily: 'var(--font-num)', background: 'var(--surface)', color: 'var(--text-1)', outline: 'none', fontWeight: 600 }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                    Sales Prefix
                  </label>
                  <input
                    value={salesPrefix}
                    onChange={e => setSalesPrefix(e.target.value.toUpperCase())}
                    maxLength={6}
                    placeholder="SL"
                    style={{ width: '100%', minWidth: 70, padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: 13, fontFamily: 'var(--font-num)', background: 'var(--surface)', color: 'var(--text-1)', outline: 'none', fontWeight: 600 }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                    Code Prefix
                  </label>
                  <input
                    value={itemCodeStart}
                    onChange={e => setItemCodeStart(e.target.value.replace(/[^0-9]/g, ''))}
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="20000"
                    style={{ width: '100%', minWidth: 70, padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', fontSize: 13, fontFamily: 'var(--font-num)', background: 'var(--surface)', color: 'var(--text-1)', outline: 'none', fontWeight: 600 }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-3)', paddingBottom: 10, whiteSpace: 'nowrap', overflowX: 'auto' }}>
                  Saved as{' '}
                  <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-2)', fontWeight: 600 }}>
                    {(certifiedPrefix || 'PC')}-{new Date().getFullYear()}-0001
                  </span>
                  {' '}· shown as{' '}
                  <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-2)', fontWeight: 600 }}>
                    {(certifiedPrefix || 'PC')}-0001
                  </span>
                </div>
              </div>
              <p className="form-hint" style={{ marginTop: 4 }}>
                Code Prefix is where new item Codes start. 20000 gives 20001, 20002 and so on.
              </p>
            </div>
          )}

          {activeTab === 'invoicing' && (
            <div className="settings-compact-form">
              <div className="card-title"><IconInvoice /> Invoicing</div>
              <p className="set-intro">These details print on your customer invoices.</p>

              <SettingsSection title="Branding">
                <div className="set-upload-row">
                  <ImageUploadTile
                    label="Logo" value={invLogoDataUrl} error={logoError}
                    onFile={f => handleImageFile(f, setInvLogoDataUrl, setLogoError)} onRemove={() => setInvLogoDataUrl('')}
                  />
                  <ImageUploadTile
                    label="Footer Image" value={invRjcCertDataUrl} error={rjcCertError} wide
                    onFile={f => handleImageFile(f, setInvRjcCertDataUrl, setRjcCertError)} onRemove={() => setInvRjcCertDataUrl('')}
                  />
                </div>
              </SettingsSection>

              <SettingsSection title="Company">
                <div className="form-row-2">
                  <FormField label="Company Name">
                    <input value={invCompanyName} onChange={e => setInvCompanyName(e.target.value)} placeholder={business?.businessName ?? 'ABC Diamonds Ltd.'} />
                  </FormField>
                  <FormField label="VAT / Registration Number">
                    <input value={invVatNumber} onChange={e => setInvVatNumber(e.target.value)} placeholder="BE0123456789" />
                  </FormField>
                </div>
              </SettingsSection>

              <SettingsSection title="Address">
                <div className="form-row-2">
                  <FormField label="Address Line 1">
                    <input value={invAddressLine1} onChange={e => setInvAddressLine1(e.target.value)} placeholder="Street and number" />
                  </FormField>
                  <FormField label="Address Line 2">
                    <input value={invAddressLine2} onChange={e => setInvAddressLine2(e.target.value)} placeholder="Suite, unit, floor…" />
                  </FormField>
                </div>
                <div className="form-row-3">
                  <FormField label="Postal Code">
                    <input value={invPostalCode} onChange={e => setInvPostalCode(e.target.value)} placeholder="2018" />
                  </FormField>
                  <FormField label="City">
                    <input value={invCity} onChange={e => setInvCity(e.target.value)} placeholder="Antwerp" />
                  </FormField>
                  <FormField label="Country">
                    <EntityCombobox
                      entities={COUNTRY_OPTIONS}
                      value={invCountry}
                      onChange={setInvCountry}
                      placeholder="Type to search country…"
                    />
                  </FormField>
                </div>
              </SettingsSection>

              <SettingsSection title="Contact">
                <div className="form-row-2">
                  <FormField label="Invoice Email">
                    <input type="email" value={invEmail} onChange={e => setInvEmail(e.target.value)} placeholder="finance@company.com" />
                  </FormField>
                  <FormField label="Invoice Phone">
                    <input value={invPhone} onChange={e => setInvPhone(e.target.value)} placeholder="+32 123 456 789" />
                  </FormField>
                </div>
              </SettingsSection>

              <SettingsSection title="Payment & Footer">
                <FormField label="Payment Account">
                  <select value={invBankAccountId} onChange={e => setInvBankAccountId(e.target.value)}>
                    <option value="">
                      {bankAccounts.find(a => a.isDefault) ? `Default: ${bankAccounts.find(a => a.isDefault)!.name}` : 'No default account set'}
                    </option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name}{a.iban ? ` (${a.iban})` : ''}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Invoice Footer Note">
                  <textarea value={invFooterNote} onChange={e => setInvFooterNote(e.target.value)} placeholder="Thank you for your business." style={{ minHeight: 70 }} />
                </FormField>
              </SettingsSection>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              <AccentPicker />
              <div style={{ height: 1, background: 'var(--border)' }} />
              <WallpaperPicker />
            </div>
          )}

          {activeTab === 'custom' && (
            <div>
              <div className="card-title"><IconTune /> Custom Options</div>

              <SettingsSection title="Loose Stone Grades" desc="Empty uses the normal grades." wide>
                  <OptionEditor
                    label="Color Options"
                    items={looseColors}
                    onAdd={v => setLooseColors(prev => [...prev, v])}
                    onRemove={v => setLooseColors(prev => prev.filter(x => x !== v))}
                  />
                  <OptionEditor
                    label="Clarity Options"
                    items={looseClarities}
                    onAdd={v => setLooseClarities(prev => [...prev, v])}
                    onRemove={v => setLooseClarities(prev => prev.filter(x => x !== v))}
                  />
                  <OptionEditor
                    label="Size Options"
                    items={looseSizes}
                    onAdd={v => setLooseSizes(prev => [...prev, v])}
                    onRemove={v => setLooseSizes(prev => prev.filter(x => x !== v))}
                  />
                  <OptionEditor
                    label="Sub Size Options"
                    items={looseSubSizes}
                    onAdd={v => setLooseSubSizes(prev => [...prev, v])}
                    onRemove={v => setLooseSubSizes(prev => prev.filter(x => x !== v))}
                  />
                {(looseColors.length === 0 || looseClarities.length === 0) && (
                  <div className="set-note">
                    {looseColors.length === 0 && (
                      <div><strong>Color defaults:</strong> {DEFAULT_LOOSE_COLORS.join(', ')}</div>
                    )}
                    {looseClarities.length === 0 && (
                      <div><strong>Clarity defaults:</strong> {DEFAULT_LOOSE_CLARITIES.join(', ')}</div>
                    )}
                  </div>
                )}
              </SettingsSection>

              <SettingsSection title="Invoice Descriptions" wide>
                <OptionEditor
                  label="Saved Descriptions"
                  placeholder="As per approval"
                  items={saleDescriptions}
                  onAdd={v => setSaleDescriptions(prev => [...prev, v])}
                  onRemove={v => setSaleDescriptions(prev => prev.filter(x => x !== v))}
                />
              </SettingsSection>

              <SettingsSection title="Books Adjustment Code">
                <DummyCodeCard dummyInventoryItemId={dummyInventoryItemId} currency={currency} />
              </SettingsSection>

              {tradingSystem === 'indian' && (
              <SettingsSection title="GST">
                  <div className="form-row-3" style={{ maxWidth: 760 }}>
                    <FormField label="GST Rate %">
                      <input type="number" min={0} step="0.01" value={gstRate} placeholder="0.25"
                        onChange={e => setGstRate(e.target.value)} title="Starting GST for a new invoice. Same state: half CGST, half SGST. Other state: IGST." />
                    </FormField>
                    <FormField label="Our State">
                      <select value={gstState} onChange={e => setGstState(e.target.value)}>
                        <option value="">Select…</option>
                        {INDIAN_STATES.map(st => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </FormField>
                    <FormField label="GSTIN">
                      <input value={gstin} placeholder="24ABCDE1234F1Z5" maxLength={15}
                        onChange={e => setGstin(e.target.value)} style={{ textTransform: 'uppercase' }} />
                    </FormField>
                  </div>
              </SettingsSection>
              )}


            </div>
          )}

          {activeTab === 'team' && user?.role === 'owner' && <TeamPanel />}

          {activeTab === 'accounting' && user?.role === 'owner' && <AccountingPanel />}

          {activeTab === 'usage' && <UsagePanel />}

          {activeTab === 'backup' && user?.role === 'owner' && <BackupPanel />}

          {activeTab === 'security' && (
            <div>
              <div className="card-title"><IconShieldPrivacy /> Security</div>
              <TwoStepPanel />
            </div>
          )}

          {activeTab === 'subscription' && user?.role === 'owner' && <SubscriptionPanel />}

          {activeTab === 'terms' && <TermsPanel />}

          {activeTab === 'privacy' && <PrivacyPanel />}

          {/* ── Save — shared across every tab that edits BusinessSettings
                 local state, always visible in this same card so a pending
                 edit on another tab is never lost when you switch away.
                 Team's actions are each their own modal/toggle against live
                 Firestore data, not part of this local-state form, so it
                 doesn't get this shared footer. Terms/Privacy are static
                 informational text, same reason. ── */}
          {activeTab !== 'security' && activeTab !== 'subscription' && activeTab !== 'team' && activeTab !== 'accounting' && activeTab !== 'usage' && activeTab !== 'backup'
            && activeTab !== 'terms' && activeTab !== 'privacy' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save Settings
            </Button>
            {saved && (
              <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>✓ Saved</span>
            )}
            {saveError && (
              <span style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 500 }}>{saveError}</span>
            )}
          </div>
          )}
          </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
